/**
 * /about and the legal pages, one renderer for the server and the client
 * (2026-10-04).
 *
 * The server sent /about as bare paragraphs without the page's classes, and
 * the legal pages without the draft banner; the client drew its own versions,
 * with inline styles, an English "Home" in the about trail and an English
 * "Legal" masthead in every language. Both now print this markup, and the
 * client keeps the server's page on first paint.
 */
import { localeHref } from './locale-href.js';
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
/**
 * Escape, then re-open a small whitelist of inline tags: links (https and
 * mailto only, rel and target set here so no stray attribute rides along),
 * strong, em, code. The copy carries links; nothing else gets through.
 */
export function richCatalogText(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        // Quotes stay as they are, so the escaped form still carries real ".
        .replace(/&lt;a href="((?:https:\/\/|mailto:)[^"]*)"[^&]*?&gt;/g, (_m, href) => `<a href="${href}"${href.startsWith('https://') ? ' rel="noopener" target="_blank"' : ''}>`)
        .replace(/&lt;\/a&gt;/g, '</a>')
        .replace(/&lt;(\/?)(strong|em|code)&gt;/g, '<$1$2>');
}
/** Everything inside `<main>`: the about page. */
export function aboutPageHtml({ locale, t: raw }) {
    const t = (k) => richCatalogText(raw(k));
    const p = (k) => `<p>${t(k)}</p>`;
    return `
<article class="article-route about-route">
  <section class="article-hero">
    <div class="container">
      <div class="breadcrumbs">
        <a href="${esc(localeHref(locale, '/'))}">${t('nav.home')}</a> · <span>${t('about.breadcrumb')}</span>
      </div>
      <div class="article-hero__meta">
        <span class="article-tag">${t('about.hero.tag')}</span>
        <span class="article-readtime">${t('about.hero.readtime')}</span>
      </div>
      <h1 class="article-h1">${t('about.hero.h1')}</h1>
      <p class="article-lede">${t('about.hero.lede')}</p>
    </div>
  </section>

  <section class="section">
    <div class="container article-body about-body">
      <h2>${t('about.whoRuns.h2')}</h2>
      ${p('about.whoRuns.p1')}
      ${p('about.whoRuns.p2')}

      <h2>${t('about.prices.h2')}</h2>
      ${p('about.prices.p1')}
      ${p('about.prices.p2')}

      <h2>${t('about.articles.h2')}</h2>
      ${p('about.articles.p1')}
      ${p('about.articles.p2')}
      ${p('about.articles.p3')}

      <h2>${t('about.images.h2')}</h2>
      ${p('about.images.p1')}
      ${p('about.images.p2')}

      <h2>${t('about.guarantees.h2')}</h2>
      <ul>
        ${[1, 2, 3].map((n) => `<li><strong>${t(`about.guarantees.item${n}Title`)}</strong> ${t(`about.guarantees.item${n}Body`)}</li>`).join('\n        ')}
      </ul>
      ${p('about.guarantees.p')}

      <h2>${t('about.corrections.h2')}</h2>
      ${p('about.corrections.p1')}
      <ul>
        ${[1, 2, 3].map((n) => `<li>${t(`about.corrections.item${n}`)}</li>`).join('\n        ')}
      </ul>
      ${p('about.corrections.p2')}

      <h2>${t('about.notDo.h2')}</h2>
      ${p('about.notDo.p1')}
      ${p('about.notDo.p2')}
    </div>
  </section>
</article>`;
}
function legalSectionHtml(s) {
    const head = `<h2 class="article-h2">${esc(s.h)}</h2>`;
    if (Array.isArray(s.items)) {
        return `${head}\n<ul class="article-list">${s.items.map((it) => `<li>${esc(it)}</li>`).join('')}</ul>`;
    }
    if (s.p_legal || s.p_email || s.p_telegram) {
        const mail = (a) => `<a href="mailto:${esc(a)}">${esc(a)}</a>`;
        const parts = [];
        if (s.p_prefix)
            parts.push(`${esc(s.p_prefix)} `);
        if (s.p_legal)
            parts.push(mail(s.p_legal));
        if (s.p_email)
            parts.push(mail(s.p_email));
        if (s.p_support_prefix)
            parts.push(`${esc(s.p_support_prefix)} `);
        if (s.p_support)
            parts.push(mail(s.p_support));
        if (s.p_telegram_prefix)
            parts.push(`${esc(s.p_telegram_prefix)} `);
        if (s.p_telegram)
            parts.push(`<a href="https://t.me/${esc(s.p_telegram.replace(/^@/, ''))}">${esc(s.p_telegram)}</a>`);
        if (s.p_suffix)
            parts.push(esc(s.p_suffix));
        return `${head}\n<p>${parts.join('')}</p>`;
    }
    return `${head}\n<p>${esc(s.p ?? '')}</p>`;
}
/** Everything inside `<main>`: a legal document, sections in numeric order. */
export function legalPageHtml({ kind, doc, banner, volume }) {
    const sections = Object.keys(doc)
        .filter((k) => /^section\d+$/.test(k))
        .sort((a, b) => Number(a.slice(7)) - Number(b.slice(7)))
        .map((k) => doc[k])
        .filter((v) => !!v && typeof v === 'object' && 'h' in v)
        .map(legalSectionHtml)
        .join('\n');
    return `
<article class="legal-route legal-route--${kind}">
<section class="hero legal-hero">
  <div class="container">
    <div class="hero__masthead">
      <div class="hero__volume">${esc(volume)}</div>
      <div class="hero__edition">${esc(doc.title)}</div>
      <div class="hero__date">MMXXVI</div>
    </div>
    <div class="legal-hero__body">
      <h1 class="article-h1 legal-hero__title">${esc(doc.title)}</h1>
    </div>
  </div>
</section>
<section class="section">
  <div class="container">
    <div class="article-body">
      <aside class="article-callout article-callout--warn legal-draft">
        ${esc(banner.warning)}
        ${esc(banner.binding)} <a href="mailto:${esc(banner.email)}">${esc(banner.email)}</a>.
      </aside>
      <p>${esc(doc.intro)}</p>
      ${sections}
    </div>
  </div>
</section>
</article>`;
}
